import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import usePlan from '../../hooks/usePlan';
import {
  Check, X, AlertTriangle, ChevronDown, ChevronUp,
  Zap, Clock, Lock, ArrowRight, CheckCircle, CheckCircle2, ExternalLink,
  Copy, MousePointerClick, BookOpen, Headphones, Sparkles,
  Loader2, Plug, KeyRound, UserCheck, XCircle, FileCheck,
} from 'lucide-react';
import IntegrationDataPanel from '../../components/platform/IntegrationDataPanel';
import GuidedFlowModal, { hasGuidedFlow } from '../../components/platform/GuidedFlowModal';

const ACTIE_TYPES = {
  one_click: { icon: MousePointerClick, label: '1-Click', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  guided: { icon: BookOpen, label: 'Guided', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  managed: { icon: Headphones, label: 'Door AI uitgevoerd', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
};

/* Eerlijke execution status badges — matchen execution_engine statussen */
const EXEC_STATUS = {
  completed:                { label: 'Uitgevoerd', color: '#15803d', bg: '#dcfce7', border: '#86efac', icon: CheckCircle2 },
  running:                  { label: 'Wordt nu uitgevoerd', color: '#1d4ed8', bg: '#dbeafe', border: '#93c5fd', icon: Loader2, spin: true },
  queued:                   { label: 'In wachtrij', color: '#a16207', bg: '#fef9c3', border: '#fde047', icon: Clock },
  awaiting_integration:     { label: 'Wacht op koppeling', color: '#0369a1', bg: '#e0f2fe', border: '#7dd3fc', icon: Plug },
  pending_api_approval:     { label: 'Wacht op API-goedkeuring', color: '#c2410c', bg: '#ffedd5', border: '#fdba74', icon: KeyRound },
  requires_customer_action: { label: 'Uw actie nodig', color: '#6d28d9', bg: '#ede9fe', border: '#c4b5fd', icon: UserCheck },
  team_handling:            { label: 'Ops team plaatst', color: '#0e7490', bg: '#cffafe', border: '#67e8f9', icon: Headphones },
  failed:                   { label: 'Mislukt', color: '#b91c1c', bg: '#fee2e2', border: '#fca5a5', icon: XCircle },
  no_executor:              { label: 'Handmatige verwerking', color: '#475569', bg: '#f1f5f9', border: '#cbd5e1', icon: FileCheck },
};

const ActieTypeBadgeMini = ({ type }) => {
  const cfg = ACTIE_TYPES[type];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      padding: '2px 6px', borderRadius: '5px', fontSize: '9px',
      fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>
      <Icon size={10} />
      {cfg.label}
    </span>
  );
};

/* Eerlijke execution-status pill — gebruikt opdracht.execution_status */
const ExecStatusBadge = ({ status }) => {
  const cfg = EXEC_STATUS[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`exec-status-${status}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        padding: '2px 7px', borderRadius: '5px', fontSize: '9px',
        fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      }}>
      <Icon size={10} style={cfg.spin ? { animation: 'spin 1s linear infinite' } : {}} />
      {cfg.label}
    </span>
  );
};

const CATEGORY_INTEGRATIONS = {
  seo: [
    { provider: 'google_search_console', title: 'Google Search Console' },
  ],
  website: [
    { provider: 'google_analytics', title: 'Google Analytics' },
  ],
  lokaal: [
    { provider: 'google_business', title: 'Google Bedrijfsprofiel' },
  ],
  reputatie: [
    { provider: 'google_business', title: 'Google Bedrijfsprofiel' },
  ],
  advertenties: [
    { provider: 'google_ads', title: 'Google Ads' },
  ],
  social: [
    { provider: 'meta_facebook', title: 'Facebook' },
    { provider: 'meta_instagram', title: 'Instagram' },
  ],
};

const PRIORITY_COLORS = {
  kritiek: { bg: '#fef2f2', border: '#fca5a5', text: '#dc2626', label: 'Kritiek' },
  hoog: { bg: '#fff7ed', border: '#fdba74', text: '#ea580c', label: 'Hoog' },
  middel: { bg: '#fefce8', border: '#fde047', text: '#ca8a04', label: 'Middel' },
  laag: { bg: '#f0fdf4', border: '#86efac', text: '#16a34a', label: 'Laag' },
};

/* Score ring */
const Ring = ({ score, size = 80 }) => {
  const r = size / 2 - 6, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const c = score >= 70 ? '#22c55e' : score >= 50 ? '#D4A017' : '#ef4444';
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth="6" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth="6"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2+5} textAnchor="middle" fill={c} fontSize="18" fontWeight="800" fontFamily="Sora, sans-serif">{score}</text>
    </svg>
  );
};

/* Website check items shown from scan data */
function ScanCheckItem({ label, passed, detail }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
      background: passed ? '#f0fdf4' : '#fef2f2', borderRadius: '10px',
      border: `1px solid ${passed ? '#bbf7d0' : '#fecaca'}`,
    }}>
      {passed ? (
        <Check size={16} style={{ color: '#22c55e', flexShrink: 0 }} />
      ) : (
        <X size={16} style={{ color: '#ef4444', flexShrink: 0 }} />
      )}
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{label}</p>
        {detail && <p style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{detail}</p>}
      </div>
    </div>
  );
}

/* Copyable example card for DIY instructions */
function DiyExampleMini({ example }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(example.text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = example.text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="module-diy-example" style={{
      background: '#f0fdf4', borderRadius: '8px', border: '1px solid #d1fae5',
      padding: '10px 12px', marginBottom: '8px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
        <span style={{ fontSize: '10px', fontWeight: 700, color: '#059669' }}>{example.label}</span>
        <button onClick={handleCopy} style={{
          background: copied ? '#059669' : '#ecfdf5', border: `1px solid ${copied ? '#059669' : '#a7f3d0'}`,
          borderRadius: '5px', padding: '2px 6px', fontSize: '9px', fontWeight: 600,
          color: copied ? 'white' : '#059669', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0,
        }}>
          {copied ? <><CheckCircle size={9} /> Gekopieerd</> : <><Copy size={9} /> Kopieer</>}
        </button>
      </div>
      <div style={{
        background: 'white', borderRadius: '6px', padding: '8px 10px',
        fontFamily: "'Sora', monospace", fontSize: '11px', color: '#0f172a', lineHeight: 1.5,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', border: '1px dashed #a7f3d0', marginBottom: '6px',
      }}>
        {example.text}
      </div>
      {example.where && (
        <div style={{ display: 'flex', gap: '5px', alignItems: 'flex-start' }}>
          <ArrowRight size={10} style={{ color: '#94a3b8', flexShrink: 0, marginTop: '2px' }} />
          <p style={{ fontSize: '10px', color: '#64748b', lineHeight: 1.4 }}>{example.where}</p>
        </div>
      )}
    </div>
  );
}

/* Opdracht card within module page */
function ModuleOpdrachtCard({ opdracht, isPaid, locked, isManaged, onExpand, expanded, onStartGuided }) {
  const prio = PRIORITY_COLORS[opdracht.priority] || PRIORITY_COLORS.middel;
  const effectiveActieType = isManaged ? 'managed' : opdracht.actie_type;
  const execStatus = opdracht.execution_status;  // gezet door execution_engine.queue_execution_job
  const execMeta = execStatus ? EXEC_STATUS[execStatus] : null;

  return (
    <div data-testid={`module-opdracht-${opdracht.template_id}`} style={{
      background: 'white', borderRadius: '14px', border: '1px solid #f1f5f9',
      overflow: 'hidden', position: 'relative',
    }}>
      {locked && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(3px)', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: '14px',
        }}>
          <Lock size={18} style={{ color: '#94a3b8', marginBottom: '6px' }} />
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Upgrade naar Radar</span>
          <Link to="/platform/checkout" style={{
            marginTop: '6px', background: '#D4A017', color: '#0f172a', padding: '5px 12px',
            borderRadius: '8px', fontSize: '11px', fontWeight: 700, textDecoration: 'none',
          }}>Ontgrendel</Link>
        </div>
      )}

      {/* Header */}
      <div onClick={() => !locked && onExpand()} style={{
        padding: '16px', cursor: locked ? 'default' : 'pointer',
        display: 'flex', alignItems: 'flex-start', gap: '12px',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <span style={{
              padding: '2px 7px', borderRadius: '5px', fontSize: '9px', fontWeight: 700,
              background: prio.bg, color: prio.text, border: `1px solid ${prio.border}`,
              textTransform: 'uppercase',
            }}>{prio.label}</span>
            {isManaged && execStatus ? (
              <ExecStatusBadge status={execStatus} />
            ) : (
              opdracht.actie_type && <ActieTypeBadgeMini type={effectiveActieType} />
            )}
          </div>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
            {opdracht.title}
          </h3>
          {opdracht.pagina && (
            <p style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '6px' }}>
              📍 {opdracht.pagina}
            </p>
          )}
          {(opdracht.huidige_situatie || opdracht.beter_voorstel) ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '6px' }}>
              {opdracht.huidige_situatie && (
                <div style={{ background: '#fef2f2', borderLeft: '3px solid #f87171', borderRadius: '0 6px 6px 0', padding: '6px 8px' }}>
                  <p style={{ fontSize: '8px', color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Huidig</p>
                  <p style={{ fontSize: '11px', color: '#0f172a', fontStyle: 'italic', lineHeight: 1.4, marginTop: '2px' }}>
                    &ldquo;{(opdracht.huidige_situatie || '').slice(0, 120)}{opdracht.huidige_situatie?.length > 120 ? '…' : ''}&rdquo;
                  </p>
                </div>
              )}
              {opdracht.beter_voorstel && (
                <div style={{ background: '#fff8e1', borderLeft: '3px solid #D4A017', borderRadius: '0 6px 6px 0', padding: '6px 8px' }}>
                  <p style={{ fontSize: '8px', color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Beter voorstel</p>
                  <p style={{ fontSize: '11px', color: '#0f172a', fontWeight: 600, lineHeight: 1.4, marginTop: '2px' }}>
                    {(opdracht.beter_voorstel || '').slice(0, 130)}{opdracht.beter_voorstel?.length > 130 ? '…' : ''}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>
              {opdracht.description}
            </p>
          )}
        </div>
        {!locked && (
          expanded ? <ChevronUp size={16} style={{ color: '#94a3b8', flexShrink: 0 }} />
                   : <ChevronDown size={16} style={{ color: '#94a3b8', flexShrink: 0 }} />
        )}
      </div>

      {/* Expanded: DIY instructions with examples */}
      {expanded && !locked && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f8fafc' }}>
          <div style={{ padding: '12px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <Clock size={13} style={{ color: '#94a3b8' }} />
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>Geschatte tijd: {opdracht.diy_time}</span>
            </div>

            {/* Concrete voorbeeldteksten */}
            {(opdracht.diy_examples || []).length > 0 && (
              <div style={{ marginBottom: '14px' }}>
                <p style={{ fontSize: '10px', fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                  Klaar om te gebruiken
                </p>
                {opdracht.diy_examples.map((ex, i) => (
                  <DiyExampleMini key={i} example={ex} />
                ))}
              </div>
            )}

            {/* Stappenplan */}
            <p style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
              Stappenplan
            </p>
            {(opdracht.diy_steps || []).map((step, i) => (
              <div key={i} style={{
                display: 'flex', gap: '8px', padding: '8px 0',
                borderBottom: i < (opdracht.diy_steps?.length || 0) - 1 ? '1px solid #f8fafc' : 'none',
              }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                  background: '#0f172a', color: '#D4A017', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700,
                }}>{i + 1}</div>
                <p style={{ fontSize: '12px', color: '#374151', lineHeight: 1.5 }}>{step}</p>
              </div>
            ))}

            {/* Guided flow button — NIET voor managed users */}
            {!isManaged && opdracht.actie_type === 'guided' && hasGuidedFlow(opdracht.template_id) && (
              <button
                data-testid={`start-guided-${opdracht.template_id}`}
                onClick={() => onStartGuided(opdracht)}
                style={{
                  marginTop: '14px', width: '100%', padding: '10px', borderRadius: '8px',
                  border: 'none', background: '#2563eb', color: 'white', fontSize: '12px',
                  fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '6px',
                }}
              >
                <Sparkles size={14} /> Start Guided Setup
              </button>
            )}
            {isManaged && (
              <div data-testid="managed-status-inline" style={{
                marginTop: '14px', padding: '12px 14px',
                background: execMeta?.bg || '#f5f3ff',
                border: `1px solid ${execMeta?.border || '#ddd6fe'}`,
                borderRadius: '8px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 4 }}>
                  {execMeta?.icon ? (
                    React.createElement(execMeta.icon, {
                      size: 14,
                      style: { color: execMeta.color, ...(execMeta.spin ? { animation: 'spin 1s linear infinite' } : {}) },
                    })
                  ) : (
                    <Headphones size={14} style={{ color: '#7c3aed' }} />
                  )}
                  <p style={{ fontSize: '12px', color: execMeta?.color || '#5b21b6', fontWeight: 700 }}>
                    {execMeta?.label || 'Managed AI'}
                  </p>
                </div>
                <p style={{ fontSize: '11px', color: '#475569', lineHeight: 1.5, marginBottom: 6 }}>
                  {opdracht.execution_reason
                    || 'Wordt automatisch uitgevoerd door uw Managed AI — voortgang in AI Acties.'}
                </p>
                {opdracht.execution_evidence_url && execStatus === 'completed' && (
                  <a
                    data-testid="opdracht-evidence-link"
                    href={opdracht.execution_evidence_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 11, fontWeight: 700, color: '#15803d', textDecoration: 'underline',
                    }}>
                    Bekijk live resultaat <ExternalLink size={11} />
                  </a>
                )}
                <Link to="/platform/ai-acties" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 11, fontWeight: 700, color: '#7c3aed', textDecoration: 'underline',
                  marginLeft: opdracht.execution_evidence_url && execStatus === 'completed' ? 12 : 0,
                }}>
                  Alle AI Acties <ArrowRight size={11} />
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Generiek module-pagina component.
 * Props:
 * - category: 'seo' | 'website' | 'lokaal' | 'reputatie' | 'advertenties'
 * - title: Paginatitel
 * - subtitle: Omschrijving
 * - icon: Lucide icon component
 * - color: Themakleur
 * - scanChecks: functie die website_info omzet naar check items
 */
export default function ModulePage({ category, title, subtitle, icon: Icon, color, scanChecks }) {
  const { api } = useAuth();
  const plan = usePlan();
  const isManaged = plan.isManaged;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [guidedOpdracht, setGuidedOpdracht] = useState(null);

  useEffect(() => {
    api('get', `/api/platform/opdrachten?category=${category}`)
      .then(async r => {
        const dashR = await api('get', '/api/platform/dashboard');
        setData({ ...r.data, scan: dashR.data.scan, is_paid: dashR.data.is_paid });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [category]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: '32px', height: '32px', border: '3px solid #f1f5f9', borderTopColor: '#D4A017', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  const scan = data?.scan;
  const analyse = scan?.analyse || {};
  const websiteInfo = scan?.website_info || {};
  const mods = analyse.module_scores || {};
  const score = mods[category === 'lokaal' ? 'lokale_zichtbaarheid' : category]?.score || 0;
  const isPaid = data?.is_paid || plan.isPaid;
  const opdrachten = data?.opdrachten || [];
  const FREE_LIMIT = 3;

  const checks = scanChecks ? scanChecks(websiteInfo, scan?.scan_data_input || {}) : [];
  const passedCount = checks.filter(c => c.passed).length;
  const failedCount = checks.filter(c => !c.passed).length;

  return (
    <div data-testid={`module-page-${category}`}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <div style={{
          width: '48px', height: '48px', borderRadius: '14px',
          background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={22} style={{ color }} />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: '2px' }}>
            {title}
          </h1>
          <p style={{ color: '#64748b', fontSize: '13px' }}>{subtitle}</p>
        </div>
        <Ring score={score} size={64} />
      </div>

      {/* Live integratie data */}
      {(CATEGORY_INTEGRATIONS[category] || []).map(({ provider, title: t }) => (
        <IntegrationDataPanel key={provider} provider={provider} title={t} />
      ))}

      {/* Scan checks */}
      {checks.length > 0 && (
        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9', padding: '20px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
              Scan Resultaten
            </h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <span style={{ fontSize: '11px', color: '#22c55e', fontWeight: 600 }}>{passedCount} goed</span>
              <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: 600 }}>{failedCount} aandacht</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '8px' }}>
            {checks.map((check, i) => (
              <ScanCheckItem key={i} label={check.label} passed={check.passed} detail={check.detail} />
            ))}
          </div>
        </div>
      )}

      {/* Opdrachten for this category */}
      {opdrachten.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>
            Verbeterpunten ({opdrachten.length})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {opdrachten.map((o, idx) => (
              <ModuleOpdrachtCard
                key={o.id}
                opdracht={o}
                isPaid={isPaid}
                locked={!isPaid && !isManaged && idx >= FREE_LIMIT}
                isManaged={isManaged}
                expanded={expandedId === o.id}
                onExpand={() => setExpandedId(expandedId === o.id ? null : o.id)}
                onStartGuided={setGuidedOpdracht}
              />
            ))}
          </div>
        </div>
      )}

      {opdrachten.length === 0 && (
        <div style={{
          background: '#f0fdf4', borderRadius: '14px', padding: '24px', textAlign: 'center',
          border: '1px solid #bbf7d0', marginBottom: '16px',
        }}>
          <CheckCircle size={24} style={{ color: '#22c55e', margin: '0 auto 8px' }} />
          <p style={{ fontSize: '14px', fontWeight: 600, color: '#166534' }}>Geen verbeterpunten gevonden!</p>
          <p style={{ fontSize: '12px', color: '#4ade80' }}>Uw {title.toLowerCase()} is op orde.</p>
        </div>
      )}

      {/* Managed banner — voor managed users i.p.v. checkout-CTA */}
      {isManaged && opdrachten.length > 0 && (
        <div data-testid="module-managed-banner" style={{
          background: 'linear-gradient(135deg, #7c3aed, #5b21b6)', borderRadius: '16px',
          padding: '20px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '14px',
        }}>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <p style={{ color: '#fde68a', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
              Managed AI · actief
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, fontSize: '15px', fontFamily: "'Sora', sans-serif", marginBottom: '4px' }}>
              Onze AI + team werken hier automatisch aan
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', lineHeight: 1.5 }}>
              U hoeft niets te doen — voortgang ziet u in de AI Acties pagina.
            </p>
          </div>
          <Link to="/platform/ai-acties" data-testid="module-managed-acties-link" style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: 'white', color: '#7c3aed', padding: '10px 16px', borderRadius: '10px',
            textDecoration: 'none', fontSize: '12px', fontWeight: 700,
          }}>
            Bekijk AI Acties <ArrowRight size={13} />
          </Link>
        </div>
      )}

      {/* IMPERIA AI Beheer CTA — alleen voor non-managed users */}
      {!isManaged && (
      <div style={{
        background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', borderRadius: '16px',
        padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '16px',
      }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <p style={{ color: '#D4A017', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
            Alles uit handen geven?
          </p>
          <h3 style={{ color: 'white', fontWeight: 800, fontSize: '16px', fontFamily: "'Sora', sans-serif", marginBottom: '4px' }}>
            Laat alles regelen door IMPERIA AI
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', lineHeight: 1.5 }}>
            Wij beheren dagelijks uw SEO, advertenties, content en meer. U hoeft niets te doen — wij regelen alles automatisch.
          </p>
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'white', fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800 }}>
            €995,95<span style={{ fontSize: '12px', fontWeight: 400, color: 'rgba(255,255,255,0.5)' }}>/mnd excl. btw</span>
          </p>
          <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '-2px' }}>
            eerste maand — daarna €2.495,95
          </p>
          <Link to="/platform/checkout?plan=imperium_beheer" data-testid="beheer-cta" style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '8px',
            background: '#D4A017', color: '#0f172a', padding: '10px 20px', borderRadius: '10px',
            textDecoration: 'none', fontSize: '13px', fontWeight: 700,
          }}>
            In 1 klik regelen <ArrowRight size={14} />
          </Link>
        </div>
      </div>
      )}

      {/* Upgrade for free users — alleen wanneer niet betaald en niet managed */}
      {!isPaid && !isManaged && (
        <div style={{
          marginTop: '14px', background: 'white', borderRadius: '14px', border: '1px solid #f1f5f9',
          padding: '18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
              Ontgrendel alle verbeterpunten
            </p>
            <p style={{ fontSize: '11px', color: '#94a3b8' }}>
              Radar abonnement — €14,95/mnd of €99,95/jaar
            </p>
          </div>
          <Link to="/platform/checkout" style={{
            background: '#0f172a', color: 'white', padding: '8px 16px', borderRadius: '8px',
            textDecoration: 'none', fontSize: '12px', fontWeight: 700,
          }}>Upgrade</Link>
        </div>
      )}

      {/* Guided flow modal */}
      {guidedOpdracht && (
        <GuidedFlowModal
          opdracht={guidedOpdracht}
          onClose={() => setGuidedOpdracht(null)}
        />
      )}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
